import {
  buildProxiedInstance,
  dragAndDropFeature,
  expandAllFeature,
  hotkeysCoreFeature,
  ItemInstance,
  keyboardDragAndDropFeature,
  selectionFeature,
  syncDataLoaderFeature,
  TreeConfig,
  TreeInstance,
} from '@headless-tree/core';
import { useTree } from '@headless-tree/react';
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useClickOutside } from 'shared/hooks/use-click-outside';
import { useIsMultiSelectModifierPressed } from 'shared/hooks/use-is-multi-select-modifier-pressed';
import { ID } from 'shared/types/id';

import { DragPreviewProvider } from './drag-preview-context';
import { getItemPath, transformToInternalTreeItems } from './get-item-path';
import { InternalTreeItem, TreeItem, TreeItemID, TreeItemTypes } from './types';
import { useExpandedItems } from './use-expanded-items';
import { useKeyboardShortcuts } from './use-keyboard-shortcuts';
import { useMultiSelect } from './use-multi-select';
import { useOnDrop } from './use-on-drop';
import { useTreeDispatch } from './use-set-tree';
import * as Styled from './virtualized-tree-list.style';

const defaultTreeItemTypes = Object.values(TreeItemTypes);
const defaultDropTypes: Record<TreeItemTypes, TreeItemTypes[]> = {
  [TreeItemTypes.Root]: [
    TreeItemTypes.Goal,
    TreeItemTypes.LifeArea,
    TreeItemTypes.Priority,
    TreeItemTypes.Date,
    TreeItemTypes.DayPart,
    TreeItemTypes.Tag,
  ],
  [TreeItemTypes.Goal]: [TreeItemTypes.Task, TreeItemTypes.Goal],
  [TreeItemTypes.LifeArea]: [TreeItemTypes.Task, TreeItemTypes.Goal],
  [TreeItemTypes.Priority]: [TreeItemTypes.Task],
  [TreeItemTypes.Date]: [TreeItemTypes.Task],
  [TreeItemTypes.DayPart]: [TreeItemTypes.Task],
  [TreeItemTypes.Tag]: [TreeItemTypes.Task],
  [TreeItemTypes.Type]: [TreeItemTypes.Task],
  [TreeItemTypes.Section]: [TreeItemTypes.Task],
  [TreeItemTypes.NewTask]: [],
  [TreeItemTypes.Task]: [],
};

export type RenderItemProps = {
  item: InternalTreeItem;
  itemInstance: ItemInstance<InternalTreeItem>;
  tree: TreeInstance<InternalTreeItem>;
  isMultiSelectModifierPressed: boolean;
};

type ListProps = {
  tree: TreeInstance<InternalTreeItem>;
  renderItem: React.FC<RenderItemProps>;
  scrollElement?: React.RefObject<HTMLDivElement>;
  clearSelection?: () => void;
  isMultiSelectModifierPressed: boolean;
  denseFormat?: boolean;
  indentationWidth?: number;
};

const List = forwardRef<Virtualizer<HTMLDivElement, Element>, ListProps>(
  (
    {
      tree,
      renderItem: Item,
      scrollElement,
      clearSelection,
      isMultiSelectModifierPressed,
      denseFormat,
      indentationWidth,
    },
    ref,
  ) => {
    const _parentRef = useRef<HTMLDivElement>(null);
    const parentRef = scrollElement ?? _parentRef;
    const listRef = useRef<HTMLDivElement | null>(null);
    const VirtualContainer = scrollElement
      ? React.Fragment
      : Styled.VirtualizerContainer;
    const virtualContainerProps = scrollElement
      ? {}
      : { ref: parentRef, $isScrollable: true };

    const virtualizer = useVirtualizer({
      count: tree.getItems().length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 60,
      getItemKey: (index) => tree.getItems()[index].getItemData().id,
      observeElementOffset: (_, cb) => {
        const element = parentRef.current;
        if (!element) {
          return () => {};
        }

        const updateOffset = (isScrolling: boolean) => {
          const offsetTop =
            parentRef.current && listRef.current
              ? listRef.current.offsetTop
              : 0;
          const scrollY = element.scrollTop;
          // We tell the virtualizer: "We are this far into the LIST, not the PAGE"
          cb(Math.max(0, scrollY - offsetTop), isScrolling);
        };

        const scrollUpdateOffset = () => updateOffset(true);
        const resizeUpdateOffset = () => updateOffset(false);

        // Initial read
        resizeUpdateOffset();

        element.addEventListener('scroll', scrollUpdateOffset, {
          passive: true,
        });
        window.addEventListener('resize', resizeUpdateOffset);

        return () => {
          element.removeEventListener('scroll', scrollUpdateOffset);
          window.removeEventListener('resize', resizeUpdateOffset);
        };
      },
    });

    useImperativeHandle(ref, () => virtualizer);

    useClickOutside(parentRef, clearSelection, '[data-interaction-for-type]');

    // Clear selection when clicking on the background
    useEffect(() => {
      const container = parentRef.current;
      if (!container || !clearSelection) {
        return;
      }

      const handleContainerClick = (event: MouseEvent) => {
        if (event.defaultPrevented) {
          return;
        }

        const target = event.target as HTMLElement;

        // Check if the click is on an item
        // We use data-index which is present on every item container
        const isItemClick = target.closest('[data-index]');

        if (!isItemClick) {
          clearSelection();
        }
      };

      container.addEventListener('click', handleContainerClick);
      return () => {
        container.removeEventListener('click', handleContainerClick);
      };
    }, [clearSelection, parentRef]);

    const containerProps = tree.getContainerProps();

    const setVirtualizedContainerRef = (node: HTMLDivElement | null) => {
      listRef.current = node;

      const { ref: containerRef } = containerProps;
      if (typeof containerRef === 'function') {
        containerRef(node);
        return;
      }
      containerRef.current = node;
    };

    return (
      // @ts-ignore
      <VirtualContainer {...virtualContainerProps}>
        <Styled.TreeContainer
          {...containerProps}
          ref={setVirtualizedContainerRef}
          $height={virtualizer.getTotalSize()}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const item = tree.getItems()[virtualItem.index];
            const itemProps = item.getProps();

            const handleDragStart = (e: React.DragEvent) => {
              itemProps.onDragStart?.(e);
              e.dataTransfer.effectAllowed = 'move';
            };

            return (
              <Styled.ItemContainer
                {...itemProps}
                onDragStart={handleDragStart}
                key={virtualItem.key}
                data-index={virtualItem.index}
                data-item-id={item.getItemData().id}
                ref={(r) => {
                  virtualizer.measureElement(r);
                  itemProps.ref(r);
                }}
                $level={item.getItemMeta().level}
                className="fc-draggable-event"
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                }}
                $denseFormat={denseFormat}
                $indentationWidth={indentationWidth}
              >
                <Item
                  item={item.getItemData()}
                  itemInstance={item}
                  tree={tree}
                  isMultiSelectModifierPressed={isMultiSelectModifierPressed}
                />
              </Styled.ItemContainer>
            );
          })}
          <Styled.DragLine
            style={tree.getDragLineStyle()}
            $denseFormat={denseFormat}
          />
        </Styled.TreeContainer>
      </VirtualContainer>
    );
  },
);

export type VirtualizedTreeListProps = {
  items: Record<ID, TreeItem>;
  rootItemId: ID;
  renderItem: React.FC<RenderItemProps>;
  scrollElement?: React.RefObject<HTMLDivElement>;
  multiselectTypes?: TreeItemTypes[];
  dragTypes?: TreeItemTypes[];
  dropTypes?: Record<TreeItemTypes, TreeItemTypes[]>;
  onReorder?: (updatedTreeItems: TreeItem[]) => void;
  collapsedItems?: ID[];
  onCollapse?: (ids: ID[]) => void;
  denseFormat?: boolean;
  isContextMenuOpen?: boolean;
  // todo: instead give a list of non-draggable ids
  isItemDraggable?: (item: TreeItem) => boolean;
  indentationWidth?: number;
};

export type VirtualizedTreeListRef = {
  tree: TreeInstance<InternalTreeItem>;
};

export const VirtualizedTreeList = React.forwardRef<
  VirtualizedTreeListRef,
  VirtualizedTreeListProps
>(
  (
    {
      items,
      rootItemId,
      renderItem: RenderItem,
      scrollElement,
      multiselectTypes = defaultTreeItemTypes,
      dragTypes = defaultTreeItemTypes,
      dropTypes = defaultDropTypes,
      collapsedItems = [],
      onCollapse,
      onReorder,
      denseFormat,
      isContextMenuOpen,
      isItemDraggable,
      indentationWidth,
    },
    ref,
  ) => {
    const setTree = useTreeDispatch();
    // Transform items to InternalTreeItem with path-based IDs
    const internalItems = useMemo(
      () => transformToInternalTreeItems(items, rootItemId),
      [items, rootItemId],
    );

    const _items = useRef(internalItems);
    // Recently-removed items, kept as a fallback for headless-tree's internal
    // lookups between an items update and the next rebuildTree()
    const _stalePool = useRef<Record<TreeItemID, InternalTreeItem>>({});
    const _lastItems =
      useRef<Record<TreeItemID, InternalTreeItem>>(internalItems);
    const virtualizer = useRef<Virtualizer<HTMLDivElement, Element> | null>(
      null,
    );

    // feature support
    const { selectedItems, setSelectedItems, clearSelection } = useMultiSelect(
      internalItems,
      multiselectTypes,
    );
    const onDrop = useOnDrop(internalItems, onReorder);
    const { expandedItems, setExpandedItems } = useExpandedItems(
      internalItems,
      collapsedItems,
      onCollapse,
    );

    const [focusedItem, setFocusedItem] = React.useState<TreeItemID | null>(
      null,
    );

    // Filter stale IDs out of tree state BEFORE handing it to headless-tree.
    const validSelectedItems = useMemo(
      () => selectedItems.filter((id) => internalItems[id]),
      [selectedItems, internalItems],
    );
    const validExpandedItems = useMemo(
      () => expandedItems.filter((id) => internalItems[id]),
      [expandedItems, internalItems],
    );
    const validFocusedItem = useMemo(
      () => (focusedItem && internalItems[focusedItem] ? focusedItem : null),
      [focusedItem, internalItems],
    );

    const treeConfig = useMemo<
      TreeConfig<InternalTreeItem> & {
        dragAndDropConfig: { indentationWidth: number };
        setDragImage: () => {
          imgElement: HTMLElement;
          xOffset: number;
          yOffset: number;
        };
      }
    >(
      () => ({
        state: {
          selectedItems: validSelectedItems,
          expandedItems: validExpandedItems,
          focusedItem: validFocusedItem,
        },
        setSelectedItems,
        setExpandedItems,
        setFocusedItem,
        rootItemId: getItemPath(rootItemId), // Root path is just the root ID
        isItemFolder: (item) => !!item.getItemData().isFolder,
        getItemName: (item) => item.getItemData().name,
        dataLoader: {
          // Lookup order: current items first, then the stale pool for items
          // that were just removed but may still be referenced by headless-tree's internal caches
          getItem: (itemId) =>
            _items.current[itemId as TreeItemID] ??
            _stalePool.current[itemId as TreeItemID],
          getChildren: (itemId) =>
            _items.current?.[itemId as TreeItemID]?.children ??
            _stalePool.current?.[itemId as TreeItemID]?.children ??
            [],
        },
        scrollToItem: (item) =>
          virtualizer.current?.scrollToIndex(item.getItemMeta().index),
        onDrop,
        canDrag: (selectedItems) =>
          selectedItems.every((item) => {
            const isDraggable = isItemDraggable
              ? isItemDraggable(item.getItemData())
              : true;
            return isDraggable && dragTypes.includes(item.getItemData().type);
          }),
        canDrop: (items, target) => {
          const allowedTypes = dropTypes[target.item?.getItemData().type];
          if (!allowedTypes?.length) {
            return false;
          }

          return items.every((item) =>
            allowedTypes.includes(item.getItemData().type),
          );
        },
        canReorder: true,
        instanceBuilder: buildProxiedInstance,
        features: [
          syncDataLoaderFeature,
          selectionFeature,
          dragAndDropFeature,
          keyboardDragAndDropFeature,
          expandAllFeature,
          ...(isContextMenuOpen ? [] : [hotkeysCoreFeature]),
        ],
        dragAndDropConfig: {
          indentationWidth: (indentationWidth ?? 4) * 4, // Use same width as in styles (theme.spacing(4) = 16px usually)
        },
        setDragImage: () => ({
          imgElement: document.getElementById(`drag-preview-${rootItemId}`)!,
          xOffset: -10,
          yOffset: -10,
        }),
      }),
      [
        validSelectedItems,
        validExpandedItems,
        validFocusedItem,
        setSelectedItems,
        setExpandedItems,
        rootItemId,
        onDrop,
        isContextMenuOpen,
        indentationWidth,
        isItemDraggable,
        dragTypes,
        dropTypes,
      ],
    );

    const tree = useTree(treeConfig);

    // gives access to the tree for the parent component using the ref
    useImperativeHandle(ref, () => ({
      tree,
    }));

    const isMultiSelectModifierPressed = useIsMultiSelectModifierPressed();

    useKeyboardShortcuts(items, {
      onCollapse,
      onEscape: clearSelection,
    });

    // When internalItems changes, move the just-removed items into the stale
    // pool so headless-tree's internal calls can still resolve them.
    if (_lastItems.current !== internalItems) {
      for (const id in _lastItems.current) {
        if (!internalItems[id]) {
          _stalePool.current[id] = _lastItems.current[id];
        }
      }
      _lastItems.current = internalItems;
    }
    _items.current = internalItems;

    useEffect(() => {
      // Sync source state so stale IDs don't accumulate across updates
      setSelectedItems((prev) => {
        const filtered = prev.filter((id) => internalItems[id]);
        return filtered.length === prev.length ? prev : filtered;
      });
      setFocusedItem((prev) => (prev && internalItems[prev] ? prev : null));

      tree.rebuildTree();

      // rebuildTree() has resynced headless-tree's internal caches — the stale pool must be cleared
      _stalePool.current = {};
    }, [internalItems, tree, setSelectedItems, setFocusedItem]);

    useEffect(() => {
      setTree(tree);
    }, [setTree, tree]);

    const dndState = tree.getState()?.dnd;
    const draggedItems = dndState?.draggedItems?.length
      ? dndState.draggedItems
      : tree.getSelectedItems();

    return (
      <>
        {/*<AssistiveTreeDescription tree={tree} />*/}

        {/* todo: make drag-preview work better than this */}
        <Styled.DragPreviewWrapper id={`drag-preview-${rootItemId}`}>
          <DragPreviewProvider isPreview>
            {draggedItems?.length > 1 ? (
              <Styled.DragPreviewContainer>
                {`${draggedItems.length} tasks`}
              </Styled.DragPreviewContainer>
            ) : (
              draggedItems?.map((item) => (
                <Styled.DragPreviewContainer key={item.getId()}>
                  <RenderItem
                    item={item.getItemData()}
                    itemInstance={item}
                    tree={tree}
                    isMultiSelectModifierPressed={isMultiSelectModifierPressed}
                  />
                </Styled.DragPreviewContainer>
              ))
            )}
          </DragPreviewProvider>
        </Styled.DragPreviewWrapper>

        <List
          tree={tree}
          ref={virtualizer}
          renderItem={RenderItem}
          scrollElement={scrollElement}
          clearSelection={clearSelection}
          isMultiSelectModifierPressed={isMultiSelectModifierPressed}
          denseFormat={denseFormat}
          indentationWidth={indentationWidth}
        />
      </>
    );
  },
);
